import React, { useEffect, useRef, useState } from 'react';
import { MainContainer, MainHeading, ParaText } from '../../style/GlobalStyles';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { TableListItemLeftCellStyled, TableListItemRightCellStyled, TableListItemRowStyled, TableListItemStyled, TableListStyled } from '../../components/TableList/style';
import AddNewPayment from './AddNewPayment';
import UpdatePayment from './UpdatePayment';
import { useSelector, useDispatch } from 'react-redux';
import { selectPaymentData, selectPaymentStatus, selectPaymentError, initializeAsync, updateMethodAsync, addMethodAsync } from './paymentSlice';
import { useAuth } from '../../hooks/useAuth';
import { format } from 'date-fns';
import { PaymentIcon } from 'react-svg-credit-card-payment-icons';
import { post } from '../../api';
import forge from 'node-forge';
import { skeleton } from './skeleton';
import { Add } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import NoDataPresentUI from '../../components/NoDataPresentUI';
import BlankIcon from '../../icons/BlankIcon';
import CustomTooltip from '../../components/CustomTooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { isPastDate } from '../../utils/date';

const CONSTANTS_JSON_LIST = {
    heading: 'Payment Methods',
    categories: [
        {
            category: 'Entity Formation',
            list: [
                {
                    name: 'GundyRage LLC',
                    frequency: 'Recurs annually',
                    next_payment: '$99.00 on 5/16/2025',
                    card: 'Card ending in 5847',
                },
                {
                    name: 'WaffleSauce LLC',
                    frequency: 'Recurs annually',
                    next_payment: '$99.00 on 5/25/2025',
                    card: 'Card ending in 6090',
                },
            ],
        },
        {
            category: 'Registered Agent',
            list: [
                {
                    name: 'GundyRage LLC',
                    frequency: 'Recurs monthly',
                    next_payment: '$14.99 on 1/5/2025',
                    card: 'Card ending in 5847',
                },
                {
                    name: 'WaffleSauce LLC',
                    frequency: 'Recurs monthly',
                    next_payment: '$14.99 on 1/14/2025',
                    card: 'Card ending in 6090',
                },
            ],
        },
        {
            category: 'Tax Preparation',
            list: [
                {
                    name: 'GundyRage LLC',
                    frequency: 'Recurs monthly',
                    next_payment: 'N/A',
                    card: 'Card ending in 5847',
                },
                {
                    name: 'WaffleSauce LLC',
                    frequency: 'Recurs monthly',
                    next_payment: 'N/A',
                    card: 'Card ending in 6090',
                },
            ],
        },
        {
            category: 'Business Credit Website',
            list: [
                {
                    name: 'wafflesauce.com',
                    frequency: 'Recurs monthly',
                    next_payment: 'N/A',
                    card: 'Card ending in 4998',
                },
                {
                    name: 'Aaron Gunderson',
                    frequency: 'Recurs monthly',
                    next_payment: 'N/A',
                    card: 'Card ending in 4998',
                },
            ],
        },
    ],
};

function PaymentInfo() {
    const data = useSelector(selectPaymentData);
    const status = useSelector(selectPaymentStatus);
    const error = useSelector(selectPaymentError);
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));
    const { getUserDetailsNotAsync, setUserDetailsNotAsync, refreshSessionData } = useAuth();
    const userDetails = getUserDetailsNotAsync();

    const [selectedOrder, setSelectedOrder] = useState(null);
    const [key, setKey] = useState(null);
    const [payNowLoadingId, setPayNowLoadingId] = useState(null);
    const [isPayNowModalOpen, setIsPayNowModalOpen] = useState(false);
    const payNowWindowRef = useRef(null);
    const payNowPollRef = useRef(null);

    useEffect(() => {
        if (status === 'idle') {
            dispatch(initializeAsync({ accountIds: userDetails.contact.relatedAccounts?.map((item) => item.accountId) }));
        }
    }, [status, dispatch]);

    const generateAES256Key = () => {
        const key = forge.random.getBytesSync(32);
        return key;
    };

    const encryptAESKeyWithRSAPublicKey = (aesKey, rsaPublicKey) => {
        const publicKey = forge.pki.publicKeyFromPem(rsaPublicKey);
        return publicKey.encrypt(aesKey, 'RSA-OAEP'); // Use OAEP for better security
    };

    const encryptDataWithAESKey = (data, aesKey) => {
        const cipher = forge.cipher.createCipher('AES-CBC', aesKey);
        const iv = forge.random.getBytesSync(16); // Initialization Vector
        cipher.start({ iv });
        cipher.update(forge.util.createBuffer(data));
        cipher.finish();
        return {
            encryptedData: cipher.output.getBytes(),
            iv,
        };
    };

    const handlePaymentDetails = (data) => {
        const strData = JSON.stringify(data);
        const { encryptedData, iv } = encryptDataWithAESKey(strData, key);
        const newPaymentData = forge.util.encode64(
            JSON.stringify({
                encryptedData: forge.util.encode64(encryptedData),
                iv: forge.util.encode64(iv),
            })
        );

        post('custom/updatedContent', { memberId: userDetails.contact.id, data: newPaymentData, selectedOrderId: selectedOrder?.id || '' }).then((result) => {
            if (result.isSuccess) {
                if (result.data) {
                    if (result.data.startsWith('Success')) {
                        let methodId = result.data.split(':')[1];
                        let messageDetail = { variant: 'success', message: 'Successfully added the payment method.' };
                        const event = new CustomEvent('openToster', {
                            detail: messageDetail,
                        });
                        document.dispatchEvent(event);
                        const customEvent = new CustomEvent('ADD_PAYMENT_MODAL_CLOSE');
                        window.dispatchEvent(customEvent);

                        if (selectedOrder?.id) {
                            dispatch(addMethodAsync({ selectedMethod: { id: methodId }, data: data, selectedOrder: selectedOrder }));
                            setSelectedOrder(null);
                        }
                    } else {
                        let messageDetail = { variant: 'error', message: result.data };
                        const event = new CustomEvent('openToster', {
                            detail: messageDetail,
                        });
                        document.dispatchEvent(event);
                        const customEvent = new CustomEvent('ADD_PAYMENT_MODAL_STOP_LOADING');
                        window.dispatchEvent(customEvent);
                    }
                }
            }
        });
    };

    // Function to dispatch the custom event ADD_PAYMENT_MODAL_OPEN
    const addPaymentModalOpen = () => {
        post('custom/getKeyContent', { memberId: userDetails.contact.id }).then((result) => {
            if (result.isSuccess) {
                console.log('result', result);
                const aesKey = generateAES256Key();
                setKey(aesKey);
                console.log('aesKey', aesKey);
                const encryptedAESKey = encryptAESKeyWithRSAPublicKey(aesKey, forge.util.decode64(result.data));
                post('custom/setKeyContent', { memberId: userDetails.contact.id, key: forge.util.encode64(encryptedAESKey) });

                const customEvent = new CustomEvent('ADD_PAYMENT_MODAL_OPEN');
                window.dispatchEvent(customEvent);
            }
        });
    };

    // Function to dispatch the custom event UPDATE_PAYMENT_MODAL_OPEN
    const updatePaymentModalOpen = (cListItemDetails) => {
        setSelectedOrder({ ...cListItemDetails });
        // triggerCustomEvent
        const customEvent = new CustomEvent('UPDATE_PAYMENT_MODAL_OPEN');
        window.dispatchEvent(customEvent);
    };

    const clearPayNowPolling = () => {
        if (payNowPollRef.current) {
            window.clearInterval(payNowPollRef.current);
            payNowPollRef.current = null;
        }
    };

    const reloadIfWindowClosed = () => {
        if (payNowWindowRef.current && payNowWindowRef.current.closed) {
            clearPayNowPolling();
            payNowWindowRef.current = null;
            window.location.reload();
        }
    };

    const handlePayNowModalClose = () => {
        clearPayNowPolling();
        if (payNowWindowRef.current && !payNowWindowRef.current.closed) {
            payNowWindowRef.current.close();
        }
        payNowWindowRef.current = null;
        setIsPayNowModalOpen(false);
        window.location.reload();
    };

    useEffect(() => {
        const handleFocus = () => reloadIfWindowClosed();
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                reloadIfWindowClosed();
            }
        };

        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            clearPayNowPolling();
            if (payNowWindowRef.current && !payNowWindowRef.current.closed) {
                payNowWindowRef.current.close();
            }
        };
    }, []);

    const payNowModalOpen = (cListItemDetails) => {
        setSelectedOrder({ ...cListItemDetails });
        setPayNowLoadingId(cListItemDetails?.id || 'loading');
        post('custom/payNow', { memberId: userDetails.contact.id, selectedOrderId: cListItemDetails?.id || '' })
            .then((result) => {
                if (result.isSuccess && result.data && result.data !== 'Error') {
                    const paymentLink = result.data;
                    setIsPayNowModalOpen(true);
                    const paymentWindow = window.open(paymentLink, '_blank', 'noopener,noreferrer');
                    if (paymentWindow) {
                        payNowWindowRef.current = paymentWindow;
                        clearPayNowPolling();
                        payNowPollRef.current = window.setInterval(() => {
                            reloadIfWindowClosed();
                        }, 500);
                    } else {
                        payNowWindowRef.current = null;
                        let messageDetail = { variant: 'error', message: 'Please allow pop-ups to complete the payment in a new tab.' };
                        const event = new CustomEvent('openToster', {
                            detail: messageDetail,
                        });
                        document.dispatchEvent(event);
                    }
                } else {
                    let messageDetail = { variant: 'error', message: 'Error while making the payment' };
                    const event = new CustomEvent('openToster', {
                        detail: messageDetail,
                    });
                    document.dispatchEvent(event);
                }
            })
            .catch(() => {
                let messageDetail = { variant: 'error', message: 'Error while making the payment' };
                const event = new CustomEvent('openToster', {
                    detail: messageDetail,
                });
                document.dispatchEvent(event);
            })
            .finally(() => {
                setPayNowLoadingId(null);
            });
    };

    const getCategoryData = () => {
        if (data.records?.length > 0) {
            let categoryData = {};
            for (let record of data.records) {
                if (!categoryData[record.paymentTitle]) {
                    categoryData[record.paymentTitle] = [];
                }
                categoryData[record.paymentTitle].push(record);
            }
            return categoryData;
        }
        return {};
    };

    const handlePaymentMethodUpdate = (selectedCard) => {
        dispatch(updateMethodAsync({ selectedMethod: selectedCard, selectedOrder: selectedOrder }));
        setSelectedOrder(null);
    };

    if (status === 'initialLoading') return skeleton;
    // if (true) return skeleton;

    return (
        <MainContainer>
            <Box
                display={'flex'}
                justifyContent={'space-between'}
                alignItems={'start'}
                sx={{
                    '@media (max-width:1000px)': {
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        alignItems: 'flex-start',

                        '& button': {
                            marginTop: '1rem',
                        },
                    },
                }}
            >
                <Box>
                    <MainHeading>Manage Payments</MainHeading>
                    <ParaText marginTop={theme.typography.generic.xSmall}>Easily review your recurring payments and manage the payment methods on file.</ParaText>
                </Box>

                <Button variant="contained" onClick={addPaymentModalOpen} startIcon={<Add />}>
                    Add payment method
                </Button>
            </Box>

            {Object.keys(getCategoryData()).length > 0 && (
                <Box marginTop={'3rem'}>
                    {!isMobileView ? (
                        <>
                            {Object.keys(getCategoryData()).map((key, j) => (
                                <TableContainer
                                    sx={(theme) => ({
                                        border: `1px solid ${theme.palette.border.neutralPrimary}`,
                                        background: theme.palette.background.paper,
                                        borderRadius: '8px',
                                        marginTop: '2rem',
                                    })}
                                >
                                    <>
                                        <Box
                                            padding={'1rem'}
                                            sx={(theme) => ({
                                                // borderBottom: `1px solid ${theme.palette.border.neutralPrimary}`,
                                            })}
                                        >
                                            <Typography component={'h2'} fontSize={'customFontSize.large'} fontWeight={'fontWeightSemiBold'}>
                                                {key}
                                            </Typography>
                                        </Box>
                                        <Table
                                            sx={(theme) => ({
                                                borderTop: `1px solid ${theme.palette.border.neutralPrimary}`,

                                                '& .MuiTableCell-root': {
                                                    borderBottom: `1px solid ${theme.palette.border.neutralPrimary}`,
                                                    padding: '16px',
                                                    // minWidth:"200px",
                                                    // width: 350,

                                                    '&[colspan]': {
                                                        padding: '0',
                                                    },
                                                },

                                                '& .MuiTableRow-root:last-child .MuiTableCell-root': {
                                                    borderBottom: `none`,
                                                },
                                            })}
                                        >
                                            <TableBody>
                                                {getCategoryData()[key]?.map((cListItemDetails, index) => {
                                                    let isDateInPast = cListItemDetails.paymentStatus === 'Error' || cListItemDetails.paymentStatus === 'Automated Collection'; //isPastDate(cListItemDetails.nextScheduledPayment);
                                                    return (
                                                        <TableRow
                                                            sx={{
                                                                '&:last-child td, &:last-child th': { border: 0 },
                                                                ...(isDateInPast
                                                                    ? {
                                                                          background: theme.palette.background.red,
                                                                          '& .MuiTypography-root.MuiTypography-body1': {
                                                                              color: theme.palette.error.main,
                                                                          },
                                                                      }
                                                                    : {}),
                                                            }}
                                                        >
                                                            <TableCell align="start" sx={{ width: '300px' }}>
                                                                <Typography fontSize={'customFontSize.small'}>{cListItemDetails.accountName}</Typography>
                                                            </TableCell>
                                                            <TableCell align="start" sx={{ width: '300px' }}>
                                                                <Typography fontSize={'customFontSize.small'}>
                                                                    Recurs{' '}
                                                                    {cListItemDetails.paymentFrequency === 'Annual'
                                                                        ? 'annually'
                                                                        : cListItemDetails.paymentFrequency === 'Monthly'
                                                                          ? 'monthly'
                                                                          : cListItemDetails.paymentFrequency === 'Biennial'
                                                                            ? 'biennially'
                                                                            : cListItemDetails.paymentFrequency}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="start" sx={{ width: '400px' }}>
                                                                <Typography fontSize={'customFontSize.small'}>
                                                                    {isDateInPast ? 'Missed' : 'Next'} payment:{' '}
                                                                    {new Intl.NumberFormat('en-US', {
                                                                        style: 'currency',
                                                                        currency: 'USD',
                                                                        maximumFractionDigits: 2,
                                                                    }).format(cListItemDetails.chargeAmount)}
                                                                    {cListItemDetails.nextScheduledPayment && (
                                                                        <Typography
                                                                            component={'span'}
                                                                            color={'text.neutralSecondary'}
                                                                            fontSize={'customFontSize.xSmall'}
                                                                            fontWeight={'fontWeightBold'}
                                                                            lineHeight={'150%'}
                                                                        >
                                                                            {' on '} {cListItemDetails.nextScheduledPaymentString}
                                                                        </Typography>
                                                                    )}
                                                                    {isDateInPast && (
                                                                        <CustomTooltip title={'due date'}>
                                                                            <IconButton
                                                                                sx={(theme) => ({
                                                                                    marginLeft: '0.25rem',
                                                                                    color: 'error.main',
                                                                                    padding: 0,

                                                                                    '& svg': {
                                                                                        width: '16px',
                                                                                        height: '16px',
                                                                                    },
                                                                                })}
                                                                            >
                                                                                <InfoOutlinedIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </CustomTooltip>
                                                                    )}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="start">
                                                                <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                                                                    <PaymentIcon type={cListItemDetails.cardType?.toLowerCase()} format="flatRounded" width={25} />
                                                                    <Typography
                                                                        fontSize={'customFontSize.small'}
                                                                        marginLeft={'0.5rem'}
                                                                        sx={{ minWidth: '133px', color: cListItemDetails.ccExpiringSoon ? 'red' : 'inherit' }}
                                                                    >
                                                                        {'Card ending in '}
                                                                        {cListItemDetails.cardLast4}
                                                                    </Typography>{' '}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell align="start">
                                                                {isDateInPast ? (
                                                                    <Button
                                                                        sx={{ padding: '4px 8px' }}
                                                                        onClick={() => payNowModalOpen(cListItemDetails)}
                                                                        disabled={payNowLoadingId === cListItemDetails.id}
                                                                    >
                                                                        {payNowLoadingId === cListItemDetails.id ? <CircularProgress size={16} /> : 'Pay now'}
                                                                    </Button>
                                                                ) : (
                                                                    <Button sx={{ padding: '4px 8px' }} onClick={() => updatePaymentModalOpen(cListItemDetails)}>
                                                                        Update
                                                                    </Button>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </>
                                </TableContainer>
                            ))}
                        </>
                    ) : (
                        <TableListStyled>
                            {Object.keys(getCategoryData()).map((key, index) => {
                                return (
                                    <>
                                        <Typography component={'h2'} fontSize={'customFontSize.large'} fontWeight={'fontWeightSemiBold'} marginBottom={'0.8rem'} marginTop={'1.5rem'}>
                                            {key}
                                        </Typography>

                                        {getCategoryData()[key]?.map((cListItemDetails, j) => {
                                            let isDateInPast = cListItemDetails.paymentStatus === 'Error' || cListItemDetails.paymentStatus === 'Automated Collection'; //isPastDate(cListItemDetails.nextScheduledPayment);
                                            return (
                                                <TableListItemStyled
                                                    key={index}
                                                    sx={{
                                                        ...(isDateInPast
                                                            ? {
                                                                  background: theme.palette.background.red,
                                                                  '& .MuiTypography-root.MuiTypography-body1': {
                                                                      color: theme.palette.error.main,
                                                                  },
                                                              }
                                                            : {}),
                                                    }}
                                                >
                                                    <TableListItemRowStyled>
                                                        <TableListItemLeftCellStyled>Entity Name</TableListItemLeftCellStyled>
                                                        <TableListItemRightCellStyled>
                                                            <Typography fontSize={'customFontSize.small'}>{cListItemDetails.accountName}</Typography>
                                                        </TableListItemRightCellStyled>
                                                    </TableListItemRowStyled>

                                                    <TableListItemRowStyled>
                                                        <TableListItemLeftCellStyled>Frequency</TableListItemLeftCellStyled>
                                                        <TableListItemRightCellStyled>
                                                            <Typography fontSize={'customFontSize.small'}>
                                                                Recurs{' '}
                                                                {cListItemDetails.paymentFrequency === 'Annual'
                                                                    ? 'annually'
                                                                    : cListItemDetails.paymentFrequency === 'Monthly'
                                                                      ? 'monthly'
                                                                      : cListItemDetails.paymentFrequency === 'Biennial'
                                                                        ? 'biennially'
                                                                        : cListItemDetails.paymentFrequency}
                                                            </Typography>
                                                        </TableListItemRightCellStyled>
                                                    </TableListItemRowStyled>

                                                    <TableListItemRowStyled>
                                                        <TableListItemLeftCellStyled>{isDateInPast ? 'Missed' : 'Next'} Payment</TableListItemLeftCellStyled>
                                                        <TableListItemRightCellStyled>
                                                            <Typography fontSize={'customFontSize.small'}>
                                                                {new Intl.NumberFormat('en-US', {
                                                                    style: 'currency',
                                                                    currency: 'USD',
                                                                    maximumFractionDigits: 2,
                                                                }).format(cListItemDetails.chargeAmount)}
                                                                {cListItemDetails.nextScheduledPayment && (
                                                                    <Typography
                                                                        component={'span'}
                                                                        color={'text.neutralSecondary'}
                                                                        fontSize={'customFontSize.xSmall'}
                                                                        fontWeight={'fontWeightBold'}
                                                                        lineHeight={'150%'}
                                                                    >
                                                                        {' on '} {cListItemDetails.nextScheduledPaymentString}
                                                                    </Typography>
                                                                )}
                                                                {isDateInPast && (
                                                                    <CustomTooltip title={'due date'}>
                                                                        <IconButton
                                                                            sx={(theme) => ({
                                                                                marginLeft: '0.25rem',
                                                                                color: 'error.main',
                                                                                padding: 0,

                                                                                '& svg': {
                                                                                    width: '16px',
                                                                                    height: '16px',
                                                                                },
                                                                            })}
                                                                        >
                                                                            <InfoOutlinedIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </CustomTooltip>
                                                                )}
                                                            </Typography>
                                                        </TableListItemRightCellStyled>
                                                    </TableListItemRowStyled>

                                                    <TableListItemRowStyled>
                                                        <TableListItemLeftCellStyled>Card</TableListItemLeftCellStyled>
                                                        <TableListItemRightCellStyled
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            <PaymentIcon type={cListItemDetails.cardType?.toLowerCase()} format="flatRounded" width={25} />
                                                            <Typography fontSize={'customFontSize.small'} marginLeft={'0.5rem'}>
                                                                {'Card ending in '}
                                                                {cListItemDetails.cardLast4}
                                                            </Typography>{' '}
                                                        </TableListItemRightCellStyled>
                                                    </TableListItemRowStyled>

                                                    <TableListItemRowStyled>
                                                        <TableListItemRightCellStyled
                                                            sx={(theme) => ({
                                                                width: '100%',
                                                                marginTop: '0.25rem',
                                                                paddingTop: '0.25rem',
                                                                borderTop: `1px solid ${theme.palette.grey[300]}`,

                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                            })}
                                                        >
                                                            {isDateInPast ? (
                                                                <Button
                                                                    sx={{ padding: '4px 8px' }}
                                                                    onClick={() => payNowModalOpen(cListItemDetails)}
                                                                    disabled={payNowLoadingId === cListItemDetails.id}
                                                                >
                                                                    {payNowLoadingId === cListItemDetails.id ? <CircularProgress size={16} /> : 'Pay now'}
                                                                </Button>
                                                            ) : (
                                                                <Button sx={{ padding: '4px 8px' }} onClick={() => updatePaymentModalOpen(cListItemDetails)}>
                                                                    Update
                                                                </Button>
                                                            )}
                                                        </TableListItemRightCellStyled>
                                                    </TableListItemRowStyled>
                                                </TableListItemStyled>
                                            );
                                        })}
                                    </>
                                );
                            })}
                        </TableListStyled>
                    )}
                </Box>
            )}
            {Object.keys(getCategoryData()).length === 0 && (
                <Box>
                    <TableContainer
                        sx={(theme) => ({
                            border: `1px solid ${theme.palette.border.neutralPrimary}`,
                            background: theme.palette.background.paper,
                            borderRadius: '8px',
                            marginTop: '2rem',
                        })}
                    >
                        <NoDataPresentUI
                            containerSx={{
                                width: {
                                    xs: '100%',
                                    sm: '100%',
                                },
                            }}
                            heading="Recurring payments will show up here!"
                            icon={<Box component={'img'} src="/images/other/paymentBlank.jpg" sx={{ width: '200px', height: '200px' }} alt="Recurring payments will show up here!"></Box>}
                        />
                    </TableContainer>
                </Box>
            )}

            <Box marginTop={'3rem'} marginLeft={'-0.5rem'} display={'flex'} flexDirection={'column'} justifyContent={'start'} alignItems={'start'}>
                {/* <Link underline="none" component={Button} href="/account-info">
                    Update Account Information
                </Link> */}
                <Button
                    variant="text"
                    onClick={addPaymentModalOpen}
                    startIcon={<Add />}
                    sx={{
                        '& .MuiButton-icon.MuiButton-startIcon': {
                            marginRight: '0.25rem',
                        },
                    }}
                >
                    Add new payment method
                </Button>
            </Box>

            <Dialog open={isPayNowModalOpen} onClose={handlePayNowModalClose} fullWidth maxWidth="md" PaperProps={{ sx: { minHeight: '70vh' } }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" padding="1rem">
                    <Typography fontSize={'customFontSize.large'} fontWeight={'fontWeightSemiBold'}>
                        Complete Payment
                    </Typography>
                    <IconButton onClick={handlePayNowModalClose} aria-label="Close payment modal">
                        <CloseIcon />
                    </IconButton>
                </Box>
                <DialogContent sx={{ paddingTop: 0 }}>
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="300px" gap={2}>
                        <CircularProgress />
                        <Typography fontSize={'customFontSize.small'} color="text.secondary" textAlign="center">
                            The payment page opened in a new tab. Finish payment there, then click the button below (or the X) to refresh this page.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ padding: '0 1.5rem 1.5rem' }}>
                    <Button variant="contained" onClick={handlePayNowModalClose}>
                        Close & Refresh
                    </Button>
                </DialogActions>
            </Dialog>

            <AddNewPayment onPaymentDetails={handlePaymentDetails} />
            <UpdatePayment
                addPaymentModalOpen={addPaymentModalOpen}
                contactId={userDetails.contact.id}
                keyData={selectedOrder?.cardExpirationMonth + selectedOrder?.cardExpirationYear + selectedOrder?.cardLast4 + selectedOrder?.cardType}
                selectedPaymentMethodId={selectedOrder?.paymentMethodDefaultId}
                onPaymentMethodUpdate={handlePaymentMethodUpdate}
            />
        </MainContainer>
    );
}

export default PaymentInfo;
